/**
 * Stream Monitor - Main Component
 * Monitors radio streams for metadata, listeners, and song statistics
 * 
 * Refactored structure:
 * - index.js (this file) - Main orchestrator with state and API calls
 * - StationCard.jsx - Individual station display
 * - SongTable.jsx - Song statistics table
 * - StatsLegend.jsx - Ranking categories legend
 * - PeriodSelector.jsx - Period dropdown
 * - AddStationForm.jsx - New station form
 * - EditStationForm.jsx - Edit station form
 * - constants.js - Shared constants
 * - utils.js - Utility functions
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { 
  Radio, Play, Pause, Plus, RefreshCw, 
  Clock, Wifi, WifiOff, AlertTriangle, Settings
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

// Sub-components
import StatsLegend from './StatsLegend';
import StationCard from './StationCard';
import AddStationForm from './AddStationForm';
import QuickTestUrl from './QuickTestUrl';
import StationDiagnosticTool from './StationDiagnosticTool';
import OperatingHoursSelector from './OperatingHoursSelector';
import StationHealthDashboard from './StationHealthDashboard';
import ListenerReport from './ListenerReport';
import ListenerSignalQuality from './ListenerSignalQuality';
import TroubleStationsPanel from './TroubleStationsPanel';
// v2.9.150 — Shazam badge data source
import { ShazamPipelineIndicator } from './ShazamPipelineIndicator';
import { ShazamRecognitionHealthIndicator } from './ShazamRecognitionHealthIndicator';
// v5 Recognition → Persistence Funnel diagnostic (read-only)
import { useShazamModeMap } from '../../hooks/useShazamModeMap';
import WorkerHealthBanner from './WorkerHealthBanner';
// v2.13.149 (2026-02-01) — StuckStationsAlert import REMOVED at operator request
// (see rendering section below for full rationale).
import MaintenanceHealthStrip from './MaintenanceHealthStrip';
import DataHygieneStats from './DataHygieneStats';
import { DEFAULT_SCHEDULE } from './constants';

const API = process.env.REACT_APP_BACKEND_URL;

const StreamMonitor = ({ token }) => {
  // State
  const [stations, setStations] = useState([]);
  // v2.9.150 — Shazam mode map (station_id → "mandatory" | "fallback")
  // Single shared cache; passed to each StationCard via station.shazam_mode
  const { map: shazamModeMap } = useShazamModeMap(token);
  const [realtimeStatus, setRealtimeStatus] = useState([]);
  const [serviceStatus, setServiceStatus] = useState({ running: false });
  const [expandedStation, setExpandedStation] = useState(null);
  const [stationStats, setStationStats] = useState({});
  const [statsLoading, setStatsLoading] = useState({});
  const [periods, setPeriods] = useState({});
  const [sortConfigs, setSortConfigs] = useState({}); // {stationId: {key: 'artist', direction: 'asc'}}
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newStation, setNewStation] = useState({ name: '', stream_url: '', stats_url: '', metadata_api_url: '', website_url: '', listeners_api_url: '', advanced_metrics_url: '', description: '' });
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [fingerprinting, setFingerprinting] = useState(false);
  const [editingStation, setEditingStation] = useState(null);
  const [editForm, setEditForm] = useState({ 
    name: '', 
    stream_url: '',
    stats_url: '',
    metadata_api_url: '',
    website_url: '',
    listeners_api_url: '',
    advanced_metrics_url: '',
    description: '',
    data_mode: 'stream',  // Default to stream analytics
    ...DEFAULT_SCHEDULE
  });
  const [editTesting, setEditTesting] = useState(false);
  const [editTestResult, setEditTestResult] = useState(null);
  const [metadataDiagnostic, setMetadataDiagnostic] = useState(null); // {stationId, loading, result}
  const [customDateRanges, setCustomDateRanges] = useState({}); // {stationId: {start, end}}
  
  // Station Classification Filters
  const [filterMusicType, setFilterMusicType] = useState('all');
  const [filterStationGroup, setFilterStationGroup] = useState('all');

  // Memoized headers
  const headers = useMemo(() => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  }), [token]);

  // ============ ENRICHMENT STATUS MONITORING ============
  // Note: Maintenance tools moved to Admin Panel → Scheduler
  // We only monitor status here to show progress when jobs are running

  // Enrichment job state
  const [enrichmentStatus, setEnrichmentStatus] = useState(null);
  const [enrichmentPolling, setEnrichmentPolling] = useState(false);
  
  // Poll enrichment status
  const pollEnrichmentStatus = useCallback(async () => {
    try {
      const res = await fetch(
        `${API}/api/stream-monitor/enrich-release-years/status`,
        { headers }
      );
      const data = await res.json().catch(() => ({}));
      
      if (res.ok) {
        const prevStatus = enrichmentStatus?.status;
        setEnrichmentStatus(data);
        
        // Auto-start polling if we detect a running job
        if (data.status === 'running' && !enrichmentPolling) {
          setEnrichmentPolling(true);
        }
        
        // Stop polling if job is complete, stopped, or error
        if (data.status === 'complete' || data.status === 'stopped' || data.status === 'error' || data.status === 'idle') {
          if (enrichmentPolling) {
            setEnrichmentPolling(false);
          }
        }
      }
    } catch (error) {
      console.error('Error polling enrichment status:', error);
    }
  }, [headers, enrichmentStatus?.status, enrichmentPolling]);
  
  // Initial status check on mount
  useEffect(() => {
    pollEnrichmentStatus();
  }, []);
  
  // Polling effect - fast polling when running
  useEffect(() => {
    if (!enrichmentPolling) return;
    
    const interval = setInterval(pollEnrichmentStatus, 2000); // Poll every 2 seconds
    return () => clearInterval(interval);
  }, [enrichmentPolling, pollEnrichmentStatus]);
  
  // Slow polling when idle (every 30 seconds to detect if Admin started a job)
  useEffect(() => {
    if (enrichmentPolling) return; // Don't slow poll if fast polling
    
    const interval = setInterval(pollEnrichmentStatus, 30000);
    return () => clearInterval(interval);
  }, [enrichmentPolling, pollEnrichmentStatus]);
  
  const stopEnrichment = async () => {
    try {
      const res = await fetch(
        `${API}/api/stream-monitor/enrich-release-years/stop`,
        { method: 'POST', headers }
      );
      const data = await res.json().catch(() => ({}));
      
      if (res.ok && data.success) {
        toast.info('🛑 Διακοπή σε εξέλιξη...');
      }
    } catch (error) {
      console.error('Error stopping enrichment:', error);
    }
  };

  // ============ API CALLS ============

  // State for refresh button animation
  const [refreshing, setRefreshing] = useState(false);

  // v2.9.54 (2026-05-29) — Polling Health Map. Keyed by station_id.
  // Populated by /api/admin/diagnostic/polling-health-per-station?days=1.
  // Used by StationCard to render a red badge for stations with
  // no_samples_pct > 10% in the last 24h.
  const [pollingHealthMap, setPollingHealthMap] = useState({});
  // 'loading' | 'computing' | 'ok' | 'error' — keeps the Retention panel always visible.
  const [pollingHealthStatus, setPollingHealthStatus] = useState({ state: 'loading', updatedAt: null });
  const [pollingHealthMeta, setPollingHealthMeta] = useState({ build: null, schedule_filter_applied: true });
  // v2.13.52 (2026-06-22) — Per-station Songs Health Map (3-window).
  // Operator request: at-a-glance per-station colour (green/yellow/red/
  // blue) + counts for songs in last 5m / 1h / 24h, using the SAME
  // thresholds the global pipeline uses (no new logic, just exposed
  // per-station). Refreshed every 90s — cheap aggregation server-side.
  const [songsHealthMap, setSongsHealthMap] = useState({});
  // v2.9.102 — worker-health snapshot (uptime, last-tick gaps,
  // watchdog restarts). Used to surface a top-level banner when the
  // service is in a degraded state so the operator knows immediately
  // that "καταγραφή σταμάτησε" before they spot it in the chart.
  const [workerHealth, setWorkerHealth] = useState(null);

  const fetchWorkerHealth = useCallback(async () => {
    try {
      const res = await fetch(
        `${API}/api/admin/diagnostic/worker-health`,
        { headers }
      );
      if (!res.ok) return;
      const data = await res.json();
      setWorkerHealth(data);
    } catch (e) {
      // Silent — banner just won't appear.
      console.warn('[StreamMonitor] worker-health fetch failed:', e);
    }
  }, [headers]);

  useEffect(() => {
    fetchWorkerHealth();
    // Poll every 30s so a stall is visible within ~1 min.
    const id = setInterval(fetchWorkerHealth, 30 * 1000);
    return () => clearInterval(id);
  }, [fetchWorkerHealth]);

  const fetchPollingHealth = useCallback(async () => {
    try {
      // v2.9.74 (2026-06-01) — Switched data source from
      // `polling-health-per-station` to the new
      // `polls-per-song-average` endpoint which implements the
      // user-specified minimum-viable-polls model:
      //   required = floor(duration / 30) + 1
      // That endpoint also natively understands the
      // `no_listener_data: true` flag introduced by the v2.9.68
      // anti-drop fix, so plays that previously vanished are now
      // surfaced as "low data reliability" rather than missing
      // from the miss% calculation.
      //
      // We re-emit the legacy keys (no_samples_pct, total_plays,
      // avg_samples, ...) so the existing StationCard badge and
      // the TroubleStationsPanel diagnosis logic keep working
      // without a second round-trip.
      const res = await fetch(
        `${API}/api/admin/diagnostic/polls-per-song-average?hours=24`,
        { headers }
      );
      if (!res.ok) {
        setPollingHealthStatus((p) => ({ ...p, state: 'error' }));
        return;
      }
      const data = await res.json();
      if (data.computing && !data.cached_at) {
        // First computation still running on the server — keep previous data, retry soon.
        setPollingHealthStatus((p) => ({ ...p, state: p.updatedAt ? 'ok' : 'computing' }));
        setTimeout(() => fetchPollingHealthRef.current && fetchPollingHealthRef.current(), 30000);
        return;
      }
      const map = {};
      for (const row of (data.stations || [])) {
        // Hide stream-analytics stations entirely from the
        // reliability map — they don't have a listener API to
        // poll, so any "miss%" metric is meaningless for them.
        if ((row.data_mode || '') === 'stream') continue;

        const dist = row.samples_distribution || {};
        // "≥3 samples" is the bar for trustworthy retention/LSI
        // curves. A play with 0–2 polls cannot produce a reliable
        // retention shape.
        const reliablePlays =
          (dist['3-5'] || 0) + (dist['6-9'] || 0) + (dist['10+'] || 0);
        const reliablePct = row.plays_total
          ? Math.round((reliablePlays / row.plays_total) * 1000) / 10
          : 0;

        map[row.station_id] = {
          // ─── Legacy keys (kept for StationCard badge) ──────────
          no_samples_pct: row.plays_with_zero_samples_pct || 0,
          zero_only_pct: 0,
          low_samples_pct: ((dist['1-2'] || 0) / (row.plays_total || 1)) * 100,
          good_samples_pct:
            (((dist['6-9'] || 0) + (dist['10+'] || 0)) / (row.plays_total || 1))
              * 100,
          total_plays: row.plays_total,
          avg_samples: row.avg_samples_per_play,
          // We no longer know per-row URL presence here; let the
          // panel fall back to its existing diagnose() heuristics.
          has_listeners_api_url: true,
          has_stats_url: false,
          has_orb_id: false,
          schedule_start_time: null,
          schedule_end_time: null,
          schedule_enabled: true,
          total_plays_raw_24h: row.plays_total,
          no_samples_pct_raw_24h: row.plays_with_zero_samples_pct || 0,
          avg_samples_raw_24h: row.avg_samples_per_play,
          avg_samples_when_polled: row.avg_samples_per_play,
          successful_plays_pct:
            100 - (row.plays_with_zero_samples_pct || 0),

          // ─── New v2.9.74 keys (consumed by TroubleStationsPanel) ───
          health_ratio: row.health_ratio || 0,
          verdict: row.verdict,
          avg_required_samples: row.avg_required_samples,
          plays_meeting_minimum_pct: row.plays_meeting_minimum_pct || 0,
          plays_with_zero_samples_pct:
            row.plays_with_zero_samples_pct || 0,
          plays_with_reliable_metrics_pct: reliablePct,
          samples_distribution: dist,
          data_mode: row.data_mode,
          poll_interval_seconds: row.poll_interval_seconds_used,
          // ─── New v2.9.101 keys — interval drift + failure mode ──
          poll_interval_seconds_used: row.poll_interval_seconds_used,
          derived_actual_interval_seconds:
            row.derived_actual_interval_seconds ?? null,
          interval_drift_ratio: row.interval_drift_ratio ?? null,
          primary_failure_mode: row.primary_failure_mode ?? null,
          fail_open_plays_pct: row.fail_open_plays_pct ?? 0,
          inferred_plays_pct: row.inferred_plays_pct ?? 0,
          // ─── New v2.9.103 keys — auto-tune metadata ──────────────
          poll_interval_auto_tuned:
            row.poll_interval_auto_tuned ?? false,
          poll_interval_auto_tuned_at:
            row.poll_interval_auto_tuned_at ?? null,
          poll_interval_auto_tuned_from:
            row.poll_interval_auto_tuned_from ?? null,
          is_high_priority: row.is_high_priority ?? false,
        };
      }
      setPollingHealthMeta({
        build: data._build_version,
        schedule_filter_applied: true,
        polling_model: data.polling_model,
        fleet_totals: data.fleet_totals,
      });
      setPollingHealthMap(map);
      setPollingHealthStatus({ state: 'ok', updatedAt: data.cached_at || new Date().toISOString() });
    } catch (e) {
      // Keep the last good data; the panel shows the failure + last update time.
      setPollingHealthStatus((p) => ({ ...p, state: 'error' }));
      console.warn('[StreamMonitor] polling-health fetch failed:', e);
    }
  }, [headers]);
  const fetchPollingHealthRef = useRef(null);
  fetchPollingHealthRef.current = fetchPollingHealth;

  // Refresh polling-health on mount and every 5 minutes thereafter.
  // The endpoint aggregates 24h of stream_songs so we don't need to
  // hammer it — sample-level changes won't move the percentages in
  // sub-minute windows.
  useEffect(() => {
    fetchPollingHealth();
    const id = setInterval(fetchPollingHealth, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [fetchPollingHealth]);

  // v2.13.52 (2026-06-22) — Songs Health Per-Station fetcher.
  // Refreshes every 90 sec so the per-station colour badge moves
  // in near real-time. Cheap on Mongo: 3 grouped aggregations
  // over the indexed (station_id, start_time) pair.
  const fetchSongsHealth = useCallback(async () => {
    try {
      const res = await fetch(
        `${API}/api/admin/diagnostic/songs-health-per-station`,
        { headers }
      );
      if (!res.ok) return;
      const data = await res.json();
      const map = {};
      for (const row of (data.stations || [])) {
        map[row.station_id] = row;
      }
      setSongsHealthMap(map);
    } catch (e) {
      console.warn('[StreamMonitor] songs-health fetch failed:', e);
    }
  }, [headers]);

  useEffect(() => {
    fetchSongsHealth();
    const id = setInterval(fetchSongsHealth, 90 * 1000);
    return () => clearInterval(id);
  }, [fetchSongsHealth]);

  const fetchData = useCallback(async () => {
    try {
      const [stationsRes, statusRes, serviceRes] = await Promise.all([
        fetch(`${API}/api/stream-monitor/stations`, { headers }),
        fetch(`${API}/api/stream-monitor/realtime`, { headers }),
        fetch(`${API}/api/stream-monitor/service/status`, { headers })
      ]);

      if (stationsRes.ok) {
        const data = await stationsRes.json();
        const sorted = (data.stations || []).sort((a, b) => 
          (a.name || '').localeCompare(b.name || '', 'el')
        );
        setStations(sorted);
      }

      if (statusRes.ok) {
        const data = await statusRes.json();
        setRealtimeStatus(data.stations || []);
      }

      if (serviceRes.ok) {
        const data = await serviceRes.json();
        setServiceStatus(data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [headers]);

  // Full refresh: stations + realtime + service + ALL loaded station stats
  const handleFullRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      // Refresh basic data
      await fetchData();
      
      // Refresh stats of ALL stations that have been loaded
      const loadedStationIds = Object.keys(stationStats);
      if (loadedStationIds.length > 0) {
        // Refresh all in parallel
        await Promise.all(
          loadedStationIds.map(stationId => {
            const period = periods[stationId] || 'yesterday';
            return fetchStationStats(stationId, period);
          })
        );
        toast.success(`Ανανέωση ${loadedStationIds.length} σταθμών ολοκληρώθηκε`);
      } else {
        toast.success('Ανανέωση ολοκληρώθηκε');
      }
    } finally {
      setRefreshing(false);
    }
  }, [fetchData, stationStats, periods]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const fetchStationStats = async (stationId, period = 'yesterday') => {
    setStatsLoading(prev => ({ ...prev, [stationId]: true }));
    try {
      const res = await fetch(
        `${API}/api/stream-monitor/stats/${stationId}?period=${period}`,
        { headers }
      );
      if (res.ok) {
        const data = await res.json();
        setStationStats(prev => ({ ...prev, [stationId]: data }));
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setStatsLoading(prev => ({ ...prev, [stationId]: false }));
    }
  };

  // ============ HANDLERS ============

  const toggleExpand = (stationId) => {
    if (expandedStation === stationId) {
      setExpandedStation(null);
    } else {
      setExpandedStation(stationId);
      const period = periods[stationId] || 'yesterday';
      if (!stationStats[stationId]) {
        fetchStationStats(stationId, period);
      }
    }
  };

  const changePeriod = (stationId, period, customRange = null) => {
    setPeriods(prev => ({ ...prev, [stationId]: period }));
    
    // Store custom date range if provided
    if (customRange) {
      setCustomDateRanges(prev => ({ ...prev, [stationId]: customRange }));
    }
    
    fetchStationStats(stationId, period);
  };

  const testStreamUrl = async () => {
    if (!newStation.stream_url) {
      toast.error('Εισάγετε URL');
      return;
    }
    setTesting(true);
    setTestResult(null);
    
    // Use XMLHttpRequest to avoid "Response body already used" errors
    // This happens due to rrweb recorder intercepting fetch responses
    const xhr = new XMLHttpRequest();
    xhr.open('GET', `${API}/api/stream-monitor/test-url?url=${encodeURIComponent(newStation.stream_url)}`, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    
    xhr.onload = function() {
      let data = {};
      try {
        data = JSON.parse(xhr.responseText);
      } catch {
        data = { detail: xhr.responseText || 'Μη αναγνώσιμη απάντηση' };
      }
      
      if (xhr.status >= 200 && xhr.status < 300) {
        setTestResult({ success: true, metadata: data.metadata });
        toast.success('URL έγκυρο!');
      } else {
        setTestResult({ success: false, error: data.detail || 'Άγνωστο σφάλμα' });
        toast.error(data.detail || 'URL μη έγκυρο');
      }
      setTesting(false);
    };
    
    xhr.onerror = function() {
      toast.error('Σφάλμα σύνδεσης');
      setTestResult({ success: false, error: 'Σφάλμα σύνδεσης' });
      setTesting(false);
    };
    
    xhr.ontimeout = function() {
      toast.error('Timeout - δοκιμάστε ξανά');
      setTestResult({ success: false, error: 'Timeout' });
      setTesting(false);
    };
    
    xhr.timeout = 30000;
    xhr.send();
  };

  const testEditUrl = async () => {
    if (!editForm.stream_url) {
      toast.error('Εισάγετε URL');
      return;
    }
    setEditTesting(true);
    setEditTestResult(null);
    
    // Use XMLHttpRequest to avoid "Response body already used" errors
    const xhr = new XMLHttpRequest();
    xhr.open('GET', `${API}/api/stream-monitor/test-url?url=${encodeURIComponent(editForm.stream_url)}`, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    
    xhr.onload = function() {
      let data = {};
      try {
        data = JSON.parse(xhr.responseText);
      } catch {
        data = { detail: xhr.responseText || 'Μη αναγνώσιμη απάντηση' };
      }
      
      if (xhr.status >= 200 && xhr.status < 300) {
        setEditTestResult({ success: true, metadata: data.metadata });
        toast.success('URL έγκυρο!');
      } else {
        setEditTestResult({ success: false, error: data.detail || 'Άγνωστο σφάλμα' });
        toast.error(data.detail || 'URL μη έγκυρο');
      }
      setEditTesting(false);
    };
    
    xhr.onerror = function() {
      toast.error('Σφάλμα σύνδεσης');
      setEditTestResult({ success: false, error: 'Σφάλμα σύνδεσης' });
      setEditTesting(false);
    };
    
    xhr.ontimeout = function() {
      toast.error('Timeout - δοκιμάστε ξανά');
      setEditTestResult({ success: false, error: 'Timeout' });
      setEditTesting(false);
    };
    
    xhr.timeout = 30000;
    xhr.send();
  };

  const fingerprintUrl = async () => {
    if (!newStation.stream_url) {
      toast.error('Εισάγετε URL');
      return;
    }
    setFingerprinting(true);
    setTestResult(null); // Clear previous result
    toast.info('Αναγνώριση τραγουδιού... (~15 δευτ.)', { duration: 20000 });
    
    // Use XMLHttpRequest to avoid "Response body already used" errors
    const xhr = new XMLHttpRequest();
    xhr.open('GET', `${API}/api/stream-monitor/fingerprint?url=${encodeURIComponent(newStation.stream_url)}`, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    
    xhr.onload = function() {
      let data = {};
      try {
        data = JSON.parse(xhr.responseText);
      } catch {
        data = { detail: xhr.responseText || 'Μη αναγνώσιμη απάντηση' };
      }
      
      if (xhr.status >= 200 && xhr.status < 300 && data.identified) {
        const { artist, title, duration_seconds } = data.identified;
        toast.success(
          `🎵 ${artist} - ${title}${duration_seconds ? ` (${Math.floor(duration_seconds/60)}:${String(duration_seconds%60).padStart(2,'0')})` : ''}`,
          { duration: 10000 }
        );
        setTestResult({
          success: true,
          metadata: { title: `${artist} - ${title}`, fingerprint: true, duration_seconds }
        });
      } else {
        const errorMsg = data.detail || 'Δεν αναγνωρίστηκε';
        toast.error(errorMsg);
        setTestResult({ success: false, error: errorMsg });
      }
      setFingerprinting(false);
    };
    
    xhr.onerror = function() {
      toast.error('Σφάλμα σύνδεσης');
      setTestResult({ success: false, error: 'Σφάλμα σύνδεσης' });
      setFingerprinting(false);
    };
    
    xhr.ontimeout = function() {
      toast.error('Timeout - δοκιμάστε ξανά');
      setTestResult({ success: false, error: 'Timeout' });
      setFingerprinting(false);
    };
    
    xhr.timeout = 30000;
    xhr.send();
  };

  const fingerprintEditUrl = async () => {
    if (!editForm.stream_url) {
      toast.error('Εισάγετε URL');
      return;
    }
    setEditTesting(true);
    setEditTestResult(null); // Clear previous result
    toast.info('Αναγνώριση τραγουδιού... (~15 δευτ.)', { duration: 20000 });
    
    // Use XMLHttpRequest to avoid "Response body already used" errors
    // This can happen with certain browser extensions or proxies
    const xhr = new XMLHttpRequest();
    xhr.open('GET', `${API}/api/stream-monitor/fingerprint?url=${encodeURIComponent(editForm.stream_url)}`, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    
    xhr.onload = function() {
      let data = {};
      try {
        data = JSON.parse(xhr.responseText);
      } catch {
        data = { detail: xhr.responseText || 'Μη αναγνώσιμη απάντηση' };
      }
      
      if (xhr.status >= 200 && xhr.status < 300 && data.identified) {
        const { artist, title, duration_seconds } = data.identified;
        toast.success(
          `🎵 ${artist} - ${title}${duration_seconds ? ` (${Math.floor(duration_seconds/60)}:${String(duration_seconds%60).padStart(2,'0')})` : ''}`,
          { duration: 10000 }
        );
        setEditTestResult({
          success: true,
          metadata: { title: `${artist} - ${title}`, fingerprint: true, duration_seconds }
        });
      } else {
        const errorMsg = data.detail || 'Δεν αναγνωρίστηκε';
        toast.error(errorMsg);
        setEditTestResult({ success: false, error: errorMsg });
      }
      setEditTesting(false);
    };
    
    xhr.onerror = function() {
      toast.error('Σφάλμα σύνδεσης');
      setEditTestResult({ success: false, error: 'Σφάλμα σύνδεσης' });
      setEditTesting(false);
    };
    
    xhr.ontimeout = function() {
      toast.error('Timeout - δοκιμάστε ξανά');
      setEditTestResult({ success: false, error: 'Timeout' });
      setEditTesting(false);
    };
    
    xhr.timeout = 30000; // 30 second timeout for fingerprinting
    xhr.send();
  };

  // Diagnostic Metadata Test - tests ALL metadata sources for a station
  const testMetadataDiagnostic = (stationId) => {
    setMetadataDiagnostic({ stationId, loading: true, result: null });
    
    const xhr = new XMLHttpRequest();
    xhr.open('GET', `${API}/api/stream-monitor/test-metadata/${stationId}`, true);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    
    xhr.onload = function() {
      try {
        const data = JSON.parse(xhr.responseText);
        setMetadataDiagnostic({ stationId, loading: false, result: data });
      } catch {
        setMetadataDiagnostic({ stationId, loading: false, result: { error: 'Μη αναγνώσιμη απάντηση' } });
      }
    };
    
    xhr.onerror = function() {
      setMetadataDiagnostic({ stationId, loading: false, result: { error: 'Σφάλμα σύνδεσης' } });
    };
    
    xhr.ontimeout = function() {
      setMetadataDiagnostic({ stationId, loading: false, result: { error: 'Timeout' } });
    };
    
    xhr.timeout = 30000;
    xhr.send();
  };

  const addStation = async () => {
    // Only require station name - URLs are optional (for metadata-only stations)
    if (!newStation.name) {
      toast.error('Συμπληρώστε το όνομα του σταθμού');
      return;
    }
    
    // Use XMLHttpRequest to avoid "Response body already used" errors
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API}/api/stream-monitor/stations`, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    
    xhr.onload = function() {
      let data = {};
      try {
        data = JSON.parse(xhr.responseText);
      } catch {
        data = { detail: xhr.responseText || 'Μη αναγνώσιμη απάντηση' };
      }
      
      if (xhr.status >= 200 && xhr.status < 300) {
        toast.success('Σταθμός αποθηκεύτηκε! Η καταγραφή θα αξιολογηθεί ξεχωριστά.');
        setNewStation({ name: '', stream_url: '', stats_url: '', metadata_api_url: '', website_url: '', listeners_api_url: '', advanced_metrics_url: '', description: '' });
        setShowAddForm(false);
        setTestResult(null);
        fetchData();
      } else {
        toast.error(data.detail || 'Σφάλμα αποθήκευσης');
      }
    };
    
    xhr.onerror = function() {
      toast.error('Σφάλμα σύνδεσης με τον server');
    };
    
    xhr.ontimeout = function() {
      toast.error('Timeout - δοκιμάστε ξανά');
    };
    
    xhr.timeout = 30000;
    xhr.send(JSON.stringify(newStation));
  };

  const startEdit = (station) => {
    setEditingStation(station._id);
    setEditForm({
      name: station.name || '',
      stream_url: station.stream_url || '',
      stats_url: station.stats_url || '',
      metadata_api_url: station.metadata_api_url || '',
      website_url: station.website_url || '',
      listeners_api_url: station.listeners_api_url || '',
      // v2.9.159 — Aggregation mode default falls back to "max" to
      // preserve historical behavior for stations created before this
      // field existed.
      listeners_aggregation_mode: station.listeners_aggregation_mode || 'max',
      advanced_metrics_url: station.advanced_metrics_url || '',
      onlineradiobox_id: station.onlineradiobox_id || '',
      mytuner_id: station.mytuner_id || '',
      metadata_source_priority: station.metadata_source_priority || 'auto',
      description: station.description || '',
      // Data mode: "live" = real listeners, "stream" = signal-based analytics
      data_mode: station.data_mode || 'stream',
      schedule_enabled: station.schedule_enabled !== false,
      schedule_days: station.schedule_days || [0, 1, 2, 3, 4, 5, 6],
      schedule_start_time: station.schedule_start_time || '06:00',
      schedule_end_time: station.schedule_end_time || '20:00',
      manual_override: station.manual_override || false,
      // Metadata source toggles - load saved values from DB
      source_icy_enabled: station.source_icy_enabled ?? true,
      source_orb_enabled: station.source_orb_enabled ?? true,
      source_mytuner_enabled: station.source_mytuner_enabled ?? true,
      // Listener thresholds
      listener_thresholds_enabled: station.listener_thresholds_enabled || false,
      listener_threshold_high: station.listener_threshold_high || null,
      listener_threshold_medium: station.listener_threshold_medium || null,
      listener_threshold_low: station.listener_threshold_low || null,
      // Radio Chart participation
      include_in_chart: station.include_in_chart || false,
      // Chart Influence (Audience Impact)
      chart_influence_level: station.chart_influence_level ?? 5,
      // Fingerprint settings
      fingerprint_enabled: station.fingerprint_enabled !== false,
      fingerprint_unlimited: station.fingerprint_unlimited === true,
      // Station Classification
      music_type: station.music_type || '',
      station_group: station.station_group || '',
      prefecture: station.prefecture || '',
      // Reverse Artist/Title fix
      reverse_artist_title: station.reverse_artist_title === true,
      // v2.9.77 — restore the previously-saved listener-sampling
      // interval into the edit form. Without this, the input always
      // showed the EditStationForm's default (30s) on re-open, even
      // after a successful save, making it look like the change had
      // been silently reverted. The backend was actually writing the
      // value correctly to MongoDB — only the edit form's initial
      // state ignored it.
      poll_interval_seconds: station.poll_interval_seconds ?? 30,
      // v2.9.78 — per-station recognition provider (default "auto").
      fingerprint_provider: station.fingerprint_provider || 'auto',
      // v2.13.140 — Shazam Fallback (ICY Stuck Rescue) manual ON/OFF.
      // Default ON — only explicit false disables it.
      icy_stuck_rescue_enabled: station.icy_stuck_rescue_enabled !== false,
      first_play_alert_enabled: station.first_play_alert_enabled === true,
      first_play_alert_emails: (station.first_play_alert_emails || []).join(', ')
    });
    setEditTestResult(null);
  };

  const cancelEdit = () => {
    setEditingStation(null);
    setEditForm({ 
      name: '', 
      stream_url: '',
      stats_url: '',
      metadata_api_url: '',
      website_url: '',
      listeners_api_url: '',
      advanced_metrics_url: '',
      description: '',
      data_mode: 'stream',
      ...DEFAULT_SCHEDULE
    });
    setEditTestResult(null);
  };

  const saveEdit = async (stationId) => {
    // Use XMLHttpRequest to avoid "Response body already used" errors
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', `${API}/api/stream-monitor/stations/${stationId}`, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    
    xhr.onload = function() {
      if (xhr.status >= 200 && xhr.status < 300) {
        toast.success('Αποθηκεύτηκε!');
        setEditingStation(null);
        setEditTestResult(null);
        fetchData();
      } else {
        let data = {};
        try {
          data = JSON.parse(xhr.responseText);
        } catch {}
        toast.error(data.detail || 'Σφάλμα αποθήκευσης');
      }
    };
    
    xhr.onerror = function() {
      toast.error('Σφάλμα σύνδεσης με τον server');
    };
    
    xhr.timeout = 30000;
    xhr.send(JSON.stringify(editForm));
  };

  const toggleStation = async (stationId, stationName, currentActive) => {
    try {
      const res = await fetch(`${API}/api/stream-monitor/stations/${stationId}/toggle`, {
        method: 'POST',
        headers
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.active ? `▶️ ${stationName} ενεργοποιήθηκε` : `⏸️ ${stationName} απενεργοποιήθηκε`);
        fetchData();
      } else {
        toast.error('Σφάλμα');
      }
    } catch (error) {
      toast.error('Σφάλμα σύνδεσης');
    }
  };

  const deleteStation = async (stationId, stationName) => {
    if (!window.confirm(`Διαγραφή "${stationName}" και όλων των δεδομένων;`)) return;
    try {
      const res = await fetch(`${API}/api/stream-monitor/stations/${stationId}`, {
        method: 'DELETE',
        headers
      });
      if (res.ok) {
        toast.success('Διαγράφηκε');
        if (expandedStation === stationId) setExpandedStation(null);
        fetchData();
      }
    } catch (error) {
      toast.error('Σφάλμα');
    }
  };

  const toggleService = async () => {
    const action = serviceStatus.running ? 'stop' : 'start';
    try {
      const res = await fetch(`${API}/api/stream-monitor/service/${action}`, {
        method: 'POST',
        headers
      });
      if (res.ok) {
        toast.success(action === 'start' ? 'Υπηρεσία ξεκίνησε' : 'Υπηρεσία σταμάτησε');
        fetchData();
      }
    } catch (error) {
      toast.error('Σφάλμα');
    }
  };

  const [exportingStationId, setExportingStationId] = useState(null);

  const exportXlsx = async (stationId, stationName) => {
    if (exportingStationId) return; // Prevent double-click
    setExportingStationId(stationId);
    const period = periods[stationId] || 'day';
    const sortConfig = sortConfigs[stationId] || { key: null, direction: 'desc' };
    
    // Build URL with sort params
    let url = `${API}/api/stream-monitor/export/${stationId}/xlsx?period=${period}`;
    if (sortConfig.key) {
      url += `&sort_by=${sortConfig.key}&sort_dir=${sortConfig.direction}`;
    }
    
    const attemptDownload = (retryCount = 0) => {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', url, true);
      xhr.responseType = 'blob';
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }
      
      xhr.onload = function() {
        if (xhr.status === 200) {
          const blob = xhr.response;
          const blobUrl = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = blobUrl;
          a.download = `${stationName}_${period}.xlsx`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(blobUrl);
          toast.success('Εξαγωγή Excel');
          setExportingStationId(null);
        } else if (xhr.status === 429 && retryCount < 2) {
          const delay = (retryCount + 1) * 3000;
          toast.info(`Επαναπροσπάθεια σε ${delay/1000}s...`);
          setTimeout(() => attemptDownload(retryCount + 1), delay);
        } else {
          toast.error('Σφάλμα εξαγωγής');
          setExportingStationId(null);
        }
      };
      
      xhr.onerror = function() {
        if (retryCount < 2) {
          setTimeout(() => attemptDownload(retryCount + 1), 3000);
        } else {
          toast.error('Σφάλμα σύνδεσης');
          setExportingStationId(null);
        }
      };
      
      xhr.send();
    };
    
    attemptDownload();
  };

  const getRealtimeInfo = (stationId) => {
    return realtimeStatus.find(s => s.station_id === stationId);
  };

  // ============ RENDER ============

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4 max-w-6xl mx-auto" data-testid="stream-monitor-page">
      {/* Header */}
      <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
        <div className="flex items-center gap-3">
          <Radio className="w-6 h-6 text-blue-500" />
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Stream Monitor</h1>
          <span className="text-sm text-gray-500 dark:text-gray-400">({stations.length} σταθμοί)</span>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Operating Hours Selector */}
          <OperatingHoursSelector
            currentStart={serviceStatus.operating_hours_start ?? 6}
            currentEnd={serviceStatus.operating_hours_end ?? 21}
            token={token}
            onUpdate={(start, end) => {
              setServiceStatus(prev => ({
                ...prev,
                operating_hours_start: start,
                operating_hours_end: end,
                operating_hours: `${start.toString().padStart(2, '0')}:00 - ${end.toString().padStart(2, '0')}:00`
              }));
            }}
          />
          
          <div className={`flex items-center gap-1.5 px-2 py-1 rounded text-sm ${
            serviceStatus.running ? 'bg-green-500/20 text-green-600 dark:text-green-400' : 'bg-red-500/20 text-red-600 dark:text-red-400'
          }`}>
            {serviceStatus.running ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
            {serviceStatus.running ? 'ON' : 'OFF'}
          </div>
          
          {!serviceStatus.running && serviceStatus.within_operating_hours && (
            <div className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-yellow-500/20 text-yellow-600 dark:text-yellow-400">
              <AlertTriangle className="w-3 h-3" />
              Διακοπή!
            </div>
          )}
          
          <Button onClick={toggleService} size="sm" variant={serviceStatus.running ? 'destructive' : 'default'}>
            {serviceStatus.running ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </Button>
          
          <Button onClick={() => setShowAddForm(!showAddForm)} size="sm" variant="outline">
            <Plus className="w-4 h-4" />
          </Button>
          
          <Button 
            onClick={handleFullRefresh} 
            size="sm" 
            variant="ghost" 
            title="Ανανέωση δεδομένων & στατιστικών"
            disabled={refreshing}
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Operating hours info */}
      <div className="text-xs text-gray-600 dark:text-gray-500 flex items-center gap-2">
        <Clock className="w-3 h-3" />
        <span>Αυτόματη λειτουργία: {serviceStatus.operating_hours || '06:00 - 21:00'}</span>
      </div>

      {/* v2.9.130 — Data Hygiene Stats (replaces the old Maintenance Health
          strip). Same compact format as Pipeline Health / Signal Quality /
          Listener Stats, click to expand into a detailed per-step breakdown
          of the nightly cleanup + write-time sanitizer counters. */}
      <DataHygieneStats token={token} />

      {/* Enrichment Progress Banner */}
      {enrichmentStatus && enrichmentStatus.status === 'running' && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />
              <span className="font-medium text-blue-700 dark:text-blue-300">
                Ενημέρωση Ετών Κυκλοφορίας σε εξέλιξη...
              </span>
            </div>
            <Button onClick={stopEnrichment} size="sm" variant="outline" className="text-red-600 border-red-300 hover:bg-red-50">
              Διακοπή
            </Button>
          </div>
          
          {/* Progress Bar */}
          <div className="w-full bg-blue-200 dark:bg-blue-800 rounded-full h-2.5 mb-2 overflow-hidden">
            <div 
              className="bg-blue-600 h-2.5 rounded-full transition-all duration-500"
              style={{ width: `${enrichmentStatus.total > 0 ? Math.min(100, Math.round((enrichmentStatus.processed / enrichmentStatus.total) * 100)) : 0}%` }}
            />
          </div>
          
          <div className="flex justify-between text-sm text-blue-600 dark:text-blue-400">
            <span>
              {enrichmentStatus.processed} / {enrichmentStatus.total} τραγούδια
              {enrichmentStatus.processed > enrichmentStatus.total && (
                <span className="text-xs text-orange-500 ml-1">(+{enrichmentStatus.processed - enrichmentStatus.total} νέα)</span>
              )}
              {enrichmentStatus.updated > 0 && ` • ${enrichmentStatus.updated} ενημερωμένα`}
            </span>
            <span>
              {enrichmentStatus.total > 0 
                ? `${Math.min(100, Math.round((enrichmentStatus.processed / enrichmentStatus.total) * 100))}%`
                : '0%'
              }
            </span>
          </div>
          
          {enrichmentStatus.current_song && (
            <div className="mt-2 text-xs text-blue-500 dark:text-blue-400 truncate">
              🎵 {enrichmentStatus.current_song}
            </div>
          )}
        </div>
      )}

      {/* Add Station Form */}
      {showAddForm && (
        <AddStationForm
          newStation={newStation}
          setNewStation={setNewStation}
          testResult={testResult}
          testing={testing}
          fingerprinting={fingerprinting}
          onTest={testStreamUrl}
          onFingerprint={fingerprintUrl}
          onAdd={addStation}
          onCancel={() => { setShowAddForm(false); setTestResult(null); }}
        />
      )}

      {/* Quick Test URL (admins) — test any stream URL without saving */}
      <div className="flex justify-end mb-1 gap-2 flex-wrap items-start">
        <StationDiagnosticTool apiBase={API} token={token} />
        <QuickTestUrl apiBase={API} token={token} />
      </div>

      {/* Stats Legend */}
      <StatsLegend />

      {/* Listener Report - above Pipeline Health */}
      <ListenerReport token={token} />

      {/* Station Health Dashboard */}
      <StationHealthDashboard token={token} onStationClick={(stationId) => {
        setExpandedStation(stationId);
        // Scroll to station after a short delay for DOM update
        setTimeout(() => {
          const el = document.querySelector(`[data-station-id="${stationId}"]`);
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 200);
      }} />

      {/* v2.9.150 — Shazam Pipeline Indicator: counts active Shazam
          stations (mandatory + fallback) with tooltip breakdown.
          2026-07-18 — Recognition Health Indicator added next to it. */}
      <div className="my-3 flex flex-wrap items-start gap-3" data-testid="shazam-pipeline-row">
        <ShazamPipelineIndicator token={token} />
        <ShazamRecognitionHealthIndicator token={token} />
      </div>

      {/* Pipeline Diagnostic v3 retired 2026-10-27: per-pod in-memory counters mislabelled as a 12h fleet view. Use the yellow-stuck-report. */}

      {/* v2.9.102 — Worker health banner: surfaces silent stalls
          (loop hung without raising) before the chart shows a gap. */}
      <WorkerHealthBanner workerHealth={workerHealth} />

      {/* v2.13.149 (2026-02-01) — StuckStationsAlert REMOVED at operator
          request: the "X/N σταθμοί χωρίς listener data" banner was
          counting ALL registered stations (including inactive / no
          listener_api configured) rather than only currently-active
          ones, so the ratio did not correlate with observed listener
          drops and produced misleading yellow alerts. The underlying
          telemetry (`snap.listener_staleness`) is still available via
          `/api/admin/diagnostic/live24-quiet-window` for operators
          who want the raw signal. */}

      {/* v2.13.56 — ManualRecoveryButton REMOVED. Operator-confirmed
          the manual reset never recovered live24 stuck stations
          because the reset cleared local cooldowns/failures but
          reused the same broken httpx pool. Only redeploy reset the
          pool. The auto-recovery layers (L6/L7/L9) for live24 are
          retired — only the L5 hourly :58 quiet window remains. */}

      {/* Listener Signal Quality - shows which stations report richer audience data */}
      <ListenerSignalQuality token={token} />

      {/* v2.9.57 — Trouble Stations Panel: surfaces Live-Audience stations
          with miss% > 10% (schedule-filtered), with one-click edit. */}
      <TroubleStationsPanel
        stations={stations}
        pollingHealthMap={pollingHealthMap}
        pollingHealthStatus={pollingHealthStatus}
        pollingHealthMeta={pollingHealthMeta}
        onEditStation={(s) => {
          startEdit(s);
          // v2.9.58 — Use the existing `data-station-id` attribute that
          // StationCard's outer wrapper already exposes (line 269). The
          // previous attempt at `data-testid="station-card-${id}"` did
          // not exist on the card, so scroll never fired.
          setTimeout(() => {
            const el = document.querySelector(
              `[data-station-id="${s._id}"]`
            );
            if (el && el.scrollIntoView) {
              el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }, 120);
        }}
        onRevertAutoTune={async (row) => {
          // v2.9.103 — Revert an auto-tuned poll_interval back to its
          // original value. Operator confirms first so we don't
          // surprise them with a 1-click downgrade of a station they
          // forgot is broken.
          const ok = window.confirm(
            `Επαναφορά poll interval για "${row.name}" από ${row.pollInterval}s πίσω στα ${row.autoTunedFrom}s;\n\n` +
            `Σημείωση: αν το πρόβλημα (drift > 3×) επιμένει, ο nightly auto-tuner θα το ξανανεβάσει σε 24h.\n` +
            `Για να το αποτρέψεις, σημείωσε τον σταθμό ως High Priority πριν την επαναφορά.`
          );
          if (!ok) return;
          try {
            const res = await fetch(
              `${API}/api/admin/diagnostic/auto-tune/revert?station_id=${row.id}`,
              { method: 'POST', headers }
            );
            const data = await res.json();
            if (data.ok) {
              toast.success(`↩ ${row.name}: επαναφορά σε ${data.reverted_to}s`);
              // Force a refresh of pollingHealthMap so the badge
              // disappears immediately.
              await fetchPollingHealth();
            } else {
              toast.error(`Αποτυχία: ${data.error || 'unknown'}`);
            }
          } catch (e) {
            toast.error(`Δικτυακό σφάλμα: ${e.message}`);
          }
        }}
      />

      {/* Station Classification Filters */}
      <div className="flex flex-wrap gap-2 items-center p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700" data-testid="station-filters">
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400 mr-1">Μουσική:</span>
        {[
          { value: 'all', label: 'Όλα' },
          { value: 'greek', label: 'Ελληνική' },
          { value: 'international', label: 'Διεθνή' },
          { value: 'rock', label: 'Ροκ' },
          { value: 'entechno', label: 'Έντεχνη' },
          { value: 'mixed', label: 'Μικτή' },
          { value: 'classic_hits', label: 'Classic Hits' },
          { value: 'sophisticated', label: 'Sophisticated' }
        ].map(opt => (
          <button
            key={opt.value}
            data-testid={`filter-music-${opt.value}`}
            onClick={() => setFilterMusicType(opt.value)}
            className={`px-2.5 py-1 text-xs rounded-full transition-colors ${
              filterMusicType === opt.value 
                ? 'bg-blue-600 text-white shadow-sm' 
                : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600'
            }`}
          >
            {opt.label}
          </button>
        ))}
        
        <span className="text-gray-300 dark:text-gray-600 mx-1">|</span>
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400 mr-1">Όμιλος:</span>
        {[
          { value: 'all', label: 'Όλοι' },
          { value: 'group_antenna', label: 'Group Antenna' },
          { value: 'group_attikes', label: 'Group Αττικές Εκδ.' },
          { value: 'group_audiomax', label: 'Group Audiomax' },
          { value: 'group_frontstage', label: 'Group Frontstage' },
          { value: 'group_kostaki', label: 'Group Κωστάκη' },
          { value: 'group_politis', label: 'Group Politis' },
          { value: 'group_skai', label: 'Group Skai' },
          { value: 'group_independent', label: 'Group Ανεξάρτητοι' },
          { value: 'unclassified', label: 'Χωρίς' }
        ].map(opt => (
          <button
            key={opt.value}
            data-testid={`filter-group-${opt.value}`}
            onClick={() => setFilterStationGroup(opt.value)}
            className={`px-2.5 py-1 text-xs rounded-full transition-colors ${
              filterStationGroup === opt.value 
                ? 'bg-emerald-600 text-white shadow-sm' 
                : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-emerald-50 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600'
            }`}
          >
            {opt.label}
          </button>
        ))}
        
        {(filterMusicType !== 'all' || filterStationGroup !== 'all') && (
          <button
            data-testid="filter-clear-all"
            onClick={() => { setFilterMusicType('all'); setFilterStationGroup('all'); }}
            className="px-2.5 py-1 text-xs rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50 ml-auto"
          >
            Καθαρισμός
          </button>
        )}
      </div>

      {/* Stations List */}
      <div className="space-y-2">
        {stations
          .filter(station => {
            if (filterMusicType !== 'all') {
              if (filterMusicType === 'unclassified') {
                if (station.music_type) return false;
              } else if (station.music_type !== filterMusicType) return false;
            }
            if (filterStationGroup !== 'all') {
              if (filterStationGroup === 'unclassified') {
                if (station.station_group) return false;
              } else if (station.station_group !== filterStationGroup) return false;
            }
            return true;
          })
          .map((station) => (
          <StationCard
            key={station._id}
            station={{
              ...station,
              customDateRange: customDateRanges[station._id],
              // v2.9.150 — inject Shazam badge mode if station uses it
              shazam_mode: shazamModeMap?.get?.(station._id),
            }}
            realtime={getRealtimeInfo(station._id)}
            pollingHealth={pollingHealthMap[station._id]}
            songsHealth={songsHealthMap[station._id]}
            isExpanded={expandedStation === station._id}
            isEditing={editingStation === station._id}
            stats={stationStats[station._id]}
            statsLoading={statsLoading[station._id]}
            period={periods[station._id] || 'yesterday'}
            token={token}
            apiUrl={API}
            sortConfig={sortConfigs[station._id] || { key: null, direction: 'desc' }}
            onSortChange={(config) => setSortConfigs(prev => ({ ...prev, [station._id]: config }))}
            editForm={editForm}
            setEditForm={setEditForm}
            editTestResult={editTestResult}
            editTesting={editTesting}
            onToggleExpand={toggleExpand}
            onChangePeriod={changePeriod}
            onToggleStation={toggleStation}
            onStartEdit={startEdit}
            onCancelEdit={cancelEdit}
            onSaveEdit={saveEdit}
            onTestEditUrl={testEditUrl}
            onFingerprintEdit={fingerprintEditUrl}
            onTestMetadata={testMetadataDiagnostic}
            metadataDiagnostic={metadataDiagnostic}
            onClearDiagnostic={() => setMetadataDiagnostic(null)}
            onDelete={deleteStation}
            onExportXlsx={exportXlsx}
            exportingStationId={exportingStationId}
          />
        ))}
        
        {stations.length === 0 && !showAddForm && (
          <div className="text-center py-8 text-gray-500">
            <Radio className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>Δεν υπάρχουν σταθμοί</p>
            <Button onClick={() => setShowAddForm(true)} size="sm" variant="outline" className="mt-2">
              <Plus className="w-4 h-4 mr-1" /> Προσθήκη
            </Button>
          </div>
        )}
      </div>

      {/* Info Footer */}
      <div className="text-xs text-gray-600 dark:text-gray-500 flex flex-wrap gap-x-4 gap-y-1 mt-4">
        <span>• Polling: 30s</span>
        <span>• Max: 100 σταθμοί</span>
        <span>• Αποθήκευση: 12 μήνες</span>
        <span>• 🎤 AudD fingerprinting</span>
      </div>
    </div>
  );
};

export default StreamMonitor;
